import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { Grid, Typography, Stack, IconButton, Box, Tooltip, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { useTranslation } from 'react-i18next';
import BasicTabs from '../../common/basic-tabs/BasicTabs';
import DetailsTabs from './components/DetailsTab';
import DocumentsTabs from './components/DocumentsTabs';
import NotesTab from '../../common/notes-tab/NotesTab';
import FormUpload from '../../common/form-upload/FormUpload';
import UploadButton from '../../common/upload-button/UploadButton';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import styles from './CustomerProfileV2.module.scss';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { RootState } from '../../store';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchDocuments, selectAllDocuments } from '../../slices/documents/documentsSlice';
import {
  fetchDocumentContracts,
  selectAllDocumentContracts,
} from '../../slices/document-contracts/documentContractsSlice';
import { selectUser } from '../../slices/user/userSlice';
import { fetchCustomers, selectCustomerById } from '../../slices/customers/customersSlice';
import CreateCustomerContactModal from '../../common/create-customer-contact-modal/CreateCustomerContactModal';
import DashboardIcon from '../../../static/assets/svg/dashboard.svg';
import BallotIcon from '../../../static/assets/svg/ballot.svg';

const CustomerProfileV2 = () => {
  const dispatch = useAppDispatch();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const company = useAppSelector(selectAllCompanies)[0];
  const formUploadRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const { customerId } = useParams();
  const parsedCustomerId = Number(customerId);
  const currentUser = useAppSelector(selectUser);
  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };
  const documentContracts = useAppSelector(selectAllDocumentContracts);
  const documentUploads = useAppSelector(selectAllDocuments);
  const combinedDocuments = [...documentContracts, ...documentUploads]
    .slice()
    .sort((a, b) => sortByDate(a, b))
    .filter((doc) => doc.customers?.includes(parsedCustomerId));

  const customer = useAppSelector((state: RootState) =>
    selectCustomerById(state, parsedCustomerId)
  );

  const [searchParams] = useSearchParams();
  const initialTab = Number(searchParams.get('initialTab')) || 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [cardView, setCardView] = useState<boolean>(true); //to refactor
  const [workflow, setWorkflow] = useState<string | null>(null);

  useEffect(() => {
    if (parsedCustomerId) {
      dispatch(fetchDocuments({ data: { user: currentUser?.id, customers: parsedCustomerId } }));
      dispatch(
        fetchDocumentContracts({ data: { user: currentUser?.id, customers: parsedCustomerId } })
      );
    }
  }, [parsedCustomerId]);

  // Second useEffect that processes data whenever the Redux store updates

  /*useEffect(() => {
    if (initialTab === 0) {
      setIsUploadOpen(true);
    }
  }, [initialTab]);*/

  useEffect(() => {
    if (company?.id) {
      dispatch(fetchCustomers({ company_id: company.id }));
    }
  }, [company]);

  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleUploadClose = () => {
    setIsUploadOpen(false);
  };

  // Actually handles both open & close
  const handleUploadOpen = () => {
    setIsUploadOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (
        targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
        targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
      ) {
        return;
      }
      if (
        formUploadRef.current &&
        !formUploadRef.current.contains(event.target as Node) &&
        uploadButtonRef.current &&
        !uploadButtonRef.current.contains(event.target as Node)
      ) {
        setIsUploadOpen(false);
      }
    };

    if (isUploadOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUploadOpen]);

  const toogleShowAddlModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleSuccessfulSubmit = () => {
    setIsModalOpen((prev) => !prev);
    setActiveTab(1);
  };

  if (!company || !customer) {
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 104px)',
        }}
      >
        <CircularProgress color='primary' size={50} />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ mt: '-92px' }}>
      <Grid item xs={12}>
        <Stack spacing={2}>
          <Typography variant='h1' component='h1' className={styles.page_title}>
            {t('headers.customerProfile')}
          </Typography>
          <Stack
            direction='row'
            justifyContent={'space-between'}
            alignItems={'center'}
            spacing={2}
            style={{ marginTop: '8px' }}
          >
            <Box ref={uploadButtonRef}>
              <UploadButton onClick={handleUploadOpen} buttonText='Upload' isArrowDown={false} />
            </Box>
            {isUploadOpen && (
              <Box className={styles.form_box} alignSelf={'flex-start'} ref={formUploadRef}>
                <FormUpload
                  onCancel={handleUploadClose}
                  categoryValue='customers'
                  customers={[customer]}
                  workflow={workflow}
                />
              </Box>
            )}
            <Tooltip title={t('labels.addPreferredContactMethod')} placement='top'>
              <IconButton
                onClick={toogleShowAddlModal}
                sx={{ marginRight: 'auto !important' }}
                className={styles.add_button}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Box display={'flex'} alignItems={'center'} gap={'8px'}>
              <span style={{ fontSize: '14px' }}>Documents view: </span>
              <Box className={styles.card_list_view_container}>
                <button
                  className={`${styles.variant_btn} + ${cardView ? styles.variant_btn_active : ''}`}
                  onClick={() => setCardView(true)}
                >
                  <DashboardIcon />
                  <span>{t('buttons.card')}</span>
                </button>
                <button
                  onClick={() => {
                    setCardView(false);
                  }}
                  className={`${styles.variant_btn} + ${
                    !cardView ? styles.variant_btn_active : ''
                  }`}
                >
                  <BallotIcon />
                  <span>{t('buttons.list')}</span>
                </button>
              </Box>
            </Box>
          </Stack>
          <Grid item xs={12}>
            <Typography variant='h2'>{customer.customer_name}</Typography>
          </Grid>
        </Stack>
      </Grid>
      <Grid item xs={12} className={styles.grid_margin_bottom}>
        <BasicTabs
          tabs={[
            /*t('businessProfile.allTab'),*/
            t('businessProfile.documentsTabs'),
            t('businessProfile.detailsTab'),
            t('businessProfile.notesTab'),
          ]}
          tabContents={[
            /*<AllTabs
              customer={customer}
              company={company}
              documents={combinedDocuments}
              key='all'
            />,*/
            <DocumentsTabs
              documents={combinedDocuments}
              key='documents'
              cardView={cardView}
              workflow={workflow}
              setWorkflow={setWorkflow}
            />,
            <DetailsTabs customer={customer} company={company} key='details' />,
            <NotesTab customer={customer} key='notes' />,
          ]}
          initialTab={initialTab}
          activeTab={activeTab}
        />
      </Grid>
      {isModalOpen && (
        <CreateCustomerContactModal
          customer={customer}
          companyId={company.id}
          open={isModalOpen}
          onClose={() => toogleShowAddlModal()}
          onSuccessfulSubmit={() => handleSuccessfulSubmit()}
        />
      )}
    </Grid>
  );
};

export default CustomerProfileV2;
